// Shared formatting helpers for the admin portal.
// Order/job documents written by the Flutter apps store dates as ISO-8601
// strings; some legacy/CF-written docs use Firestore Timestamps. Handle both.

type TimestampLike = { seconds: number } | { toDate: () => Date };

export function toDate(v: unknown): Date | null {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v === 'string') {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof v === 'object') {
    const t = v as TimestampLike;
    if ('toDate' in t && typeof t.toDate === 'function') return t.toDate();
    if ('seconds' in t && typeof t.seconds === 'number') {
      return new Date(t.seconds * 1000);
    }
  }
  return null;
}

export function formatDateTime(v: unknown): string {
  const d = toDate(v);
  if (!d) return '—';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function timeAgo(v: unknown): string {
  const d = toDate(v);
  if (!d) return '—';
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function money(dollars: unknown): string {
  if (typeof dollars !== 'number' || isNaN(dollars)) return '—';
  return `$${dollars.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Customer-facing order total: pricingBreakdown.total > finalCost > estimatedCost. */
export function orderTotal(o: Record<string, unknown>): number | null {
  const breakdown = o.pricingBreakdown as Record<string, unknown> | undefined;
  const candidates = [breakdown?.total, o.finalCost, o.estimatedCost];
  for (const c of candidates) {
    if (typeof c === 'number' && c > 0) return c;
  }
  return null;
}

/** First segment of an address ("123 Main St, Austin, TX" → "123 Main St"). */
export function shortAddress(addr: unknown): string {
  if (typeof addr !== 'string' || !addr) return '—';
  return addr.split(',')[0].trim();
}

/** Nested pickup/delivery address from order & job docs. */
export function locationAddress(o: Record<string, unknown>, key: 'pickupLocation' | 'deliveryLocation'): string {
  const loc = o[key] as Record<string, unknown> | undefined;
  return typeof loc?.address === 'string' ? loc.address : '';
}
